import React, { MouseEventHandler } from 'react';
import { CallToAction } from '../../../../commonTypes/links';
import colors from '../../../../helpers/colors';
import { generateKeyForElement } from '../../../../helpers/elementKeyHelper';
import Collapsible from '../../../Collapsible';
import Icon from '../../../Icon';
import { GridAlignedText } from '../../commonStyles/commonHeaderStyles';
import { NavSection } from '../../types';
import withOnClick from '../../withOnClick';
import LinkSections, { getSectionId } from './LinkSections';
import getCtaSectionId from './getCtaSectionId';
import {
  SimplePrimaryNavCtaSection,
  SimplePrimaryNavSubmenuHeading,
  StyledPrimaryCta,
} from './styles';
import { NavSubmenuProps } from './types';

type SimplePrimaryNavSubmenuHeaderProps = NavSubmenuProps<
  NavSection & { ctas?: CallToAction[] }
>;

const Heading: React.FC<SimplePrimaryNavSubmenuHeaderProps> = ({
  navItem: { name, sections, ctas, onCollapse, onExpand },
  toggleActive,
  isActive,
  baseId,
}) => {
  const controlledIds = sections.map((_, i) => getSectionId(baseId, name, i));
  if (ctas && ctas.length > 0) {
    controlledIds.push(getCtaSectionId(baseId, name));
  }

  const onClickWithCallback: MouseEventHandler = (event) => {
    if (onExpand && !isActive) {
      onExpand(event);
    }
    if (onCollapse && isActive) {
      onCollapse(event);
    }
    toggleActive();
  };

  return (
    <SimplePrimaryNavSubmenuHeading
      id={baseId}
      isActive={isActive}
      onClick={onClickWithCallback}
      aria-expanded={isActive}
      aria-controls={controlledIds.join(' ')}>
      <GridAlignedText>
        {name}
        <Icon name="arrow-down" size="small" flipY={isActive} color={colors.core01} />
      </GridAlignedText>
    </SimplePrimaryNavSubmenuHeading>
  );
};

type CtaSectionProps = {
  ctas: CallToAction[];
  id: string;
  resetNav: () => void;
};

const CtaSection: React.FC<CtaSectionProps> = ({ ctas, id, resetNav }) => (
  <SimplePrimaryNavCtaSection id={id}>
    {ctas.map((cta: CallToAction) => (
      <StyledPrimaryCta
        key={generateKeyForElement(cta)}
        cta={withOnClick(cta, resetNav)}
      />
    ))}
  </SimplePrimaryNavCtaSection>
);

type SimplePrimaryNavSubmenuProps = {
  resetNav: () => void;
} & SimplePrimaryNavSubmenuHeaderProps;

const SimplePrimaryNavSubmenu: React.FC<SimplePrimaryNavSubmenuProps> = ({
  navItem,
  toggleActive,
  isActive,
  baseId,
  resetNav,
}) => (
  <>
    <Heading
      baseId={baseId}
      navItem={navItem}
      isActive={isActive}
      toggleActive={toggleActive}
    />
    <Collapsible isOpened={isActive}>
      <LinkSections {...navItem} baseId={baseId} resetNav={resetNav} />
      {navItem.ctas && navItem.ctas.length > 0 && (
        <CtaSection
          id={getCtaSectionId(baseId, navItem.name)}
          ctas={navItem.ctas}
          resetNav={resetNav}
        />
      )}
    </Collapsible>
  </>
);

export default SimplePrimaryNavSubmenu;
